import { ICmsNetworkMapStations, StationsListType } from "../../Orchestrator/Types/Index";

const SHUTTLE_SLUG_MARKER = "shuttle";

/** Shuttle routes are identified by their CMS slug (e.g. "shuttle-abu-dhabi");
 *  the Station collection carries no type field.
 *
 *  Weaker than it looks, and only safe for the network map's two-section
 *  grouping: the same collection carries venues slugged `station`, `station-1`
 *  and `station-adnec` that this misses. Anything that has to answer "is this a
 *  station?" wants filterCmsStationsByStops below. */
export const isShuttleSlug = (slug: string | undefined): boolean =>
  slug?.toLowerCase().includes(SHUTTLE_SLUG_MARKER) ?? false;

export const isShuttleStation = (entry: ICmsNetworkMapStations): boolean =>
  isShuttleSlug(entry.slug);

export interface IGroupedNetworkMapStations {
  stations: ICmsNetworkMapStations[];
  shuttles: ICmsNetworkMapStations[];
}

export const groupNetworkMapStations = (
  list: ICmsNetworkMapStations[]
): IGroupedNetworkMapStations => ({
  stations: list.filter((entry) => !isShuttleStation(entry)),
  shuttles: list.filter(isShuttleStation),
});

export const chunkIntoPages = <T>(list: T[], pageSize: number): T[][] => {
  if (pageSize <= 0) {
    return list.length > 0 ? [[...list]] : [];
  }
  const pages: T[][] = [];
  for (let i = 0; i < list.length; i += pageSize) {
    pages.push(list.slice(i, i + pageSize));
  }
  return pages;
};

/**
 * The CMS `stations` collection is not a list of stations: alongside the four
 * ER stations it carries shuttle stops and partner venues (Reem Mall, ADNEC,
 * "Fujairah Shuttle Route"), because the collection has no type field for the
 * content team to set.
 *
 * `isShuttleSlug` above cannot tell them apart — it reads the slug, and two of
 * those venues are slugged `station` and `station-adnec`. The discriminator
 * that holds is the code: a real station's `station.code` (PMB, PDW, PFJ, PDD)
 * is a top-level stop in the retailer stops API, and a shuttle stop's is not.
 * `transformStops` has already folded SHT-attributed stops into `subStations`,
 * so every top-level id there is a train station.
 *
 * FAILS OPEN, unlike `stops_whitelist`: an empty stops list means the API never
 * answered, not that there are no stations, and this list is informational
 * content rather than what operations is willing to sell. Returning nothing
 * there would empty the journey planner over an unrelated outage; returning the
 * unfiltered list is exactly the behaviour that shipped before this filter.
 */
export interface IFilteredCmsStations {
  stations: ICmsNetworkMapStations[];
  /**
   * The filter could not be applied and `stations` is the unfiltered list —
   * shuttle stops and partner venues included. Callers report this: the message
   * names a page, and this helper does not know which page called it.
   */
  failedOpen: boolean;
}

export const filterCmsStationsByStops = (
  list: ICmsNetworkMapStations[] | null | undefined,
  stops: StationsListType | null | undefined
): IFilteredCmsStations => {
  if (!Array.isArray(list)) return { stations: [], failedOpen: false };
  if (!Array.isArray(stops) || stops.length === 0) {
    return { stations: [...list], failedOpen: true };
  }

  // Both sides normalised: the CMS code is hand-typed by the content team, and
  // a casing or whitespace slip there would silently drop a real station.
  const stationCodes = new Set(
    stops
      .map((stop) => String(stop?.id ?? "").trim().toUpperCase())
      .filter((code) => code.length > 0)
  );

  const filtered = list.filter((entry) =>
    stationCodes.has(String(entry?.station?.code ?? "").trim().toUpperCase())
  );

  // Zero matches is the same failure as no stops at all, one layer down: the
  // two code namespaces have stopped lining up (a locale whose CMS entries were
  // saved without a code, one typo in the admin, a deployment whose
  // NEXT_PUBLIC_STOP_EXTERNAL_CODE resolves a different mapping). Every station
  // vanishing is never the honest answer, so fail open here too rather than
  // hand back the empty list this function's own contract rules out.
  //
  // This is the likelier of the two fail-open paths in production — the codes
  // are hand-typed per locale — which is why `failedOpen` covers both rather
  // than the caller re-deriving one of them from the outside.
  if (filtered.length === 0) {
    return { stations: [...list], failedOpen: true };
  }

  return { stations: filtered, failedOpen: false };
};
